import {RGB} from "../interfaces/RGB";

const cap = (value: number, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const lerp = (from: number, to: number, t: number) => from + t * (to - from);
const fromHex = (hex: string) => parseInt(hex.substr(1), 16);
const toRGB = (color: number) => [color >> 16, (color >> 8) & 0xff, color & 0xff] as RGB;
const fromRGB = (...[red, green, blue]: RGB) => ((red << 16) + (green << 8) + blue) | 0;

/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param   Number  r       The red color value
 * @param   Number  g       The green color value
 * @param   Number  b       The blue color value
 * @return  Array           The HSL representation
 */
const rgbToHsl = (...[r, g, b]: RGB): number[] => {
    (r /= 255), (g /= 255), (b /= 255);

    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let h = (max + min) / 2;
    let s = h;
    let l = s;

    if (max == min) {
        h = s = 0; // achromatic
    } else {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    return [Math.floor(h * 360), Math.floor(s * 100), Math.floor(l * 100)];
};

const lerpColor = (color1: number, color2: number, amount: number) => {
    if (amount <= 0) {
        return color1;
    }
    if (amount >= 1) {
        return color2;
    }
    const [red1, green1, blue1] = toRGB(color1);
    const [red2, green2, blue2] = toRGB(color2);

    return fromRGB(
        lerp(red1, red2, amount),
        lerp(green1, green2, amount),
        lerp(blue1, blue2, amount)
    );
};
const paleColor = (color: number, o = 0.5) =>
    fromRGB(...(toRGB(color).map(c => Math.floor(cap(c * o, 0, 255))) as [number, number, number]));

export const color = {
    fromHex,
    toRGB,
    fromRGB,
    lerp: lerpColor,
    pale: paleColor,
    rgbToHsl
};
